import React, { useState } from "react";
import { Upload } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
console.log('BACKEND_URL:', BACKEND_URL);
console.log('API:', API);

const RegistrationSection = ({ language }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    geographicArea: '',
    mainSector: '',
    cv: null
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const content = {
    es: {
      title: "Únete a Pymetra",
      subtitle: "Completa tu registro y empieza a recibir oportunidades hoy mismo",
      fields: {
        fullName: "Nombre completo",
        email: "Email",
        geographicArea: "Zona geográfica",
        mainSector: "Sector principal",
        cv: "Adjuntar CV (PDF o DOC)"
      },
      sectors: [
        "Selecciona tu sector",
        "Tecnología",
        "Servicios profesionales",
        "Manufactura",
        "Comercio",
        "Construcción",
        "Alimentación",
        "Textil",
        "Automoción",
        "Energía",
        "Otros"
      ],
      cta: "Unirme gratis",
      successMessage: "¡Registro completado! Te contactaremos pronto con nuevas oportunidades."
    },
    en: {
      title: "Join Pymetra",
      subtitle: "Complete your registration and start receiving opportunities today",
      fields: {
        fullName: "Full name",
        email: "Email",
        geographicArea: "Geographic area",
        mainSector: "Main sector",
        cv: "Upload CV (PDF or DOC)"
      },
      sectors: [
        "Select your sector",
        "Technology",
        "Professional services",
        "Manufacturing",
        "Retail",
        "Construction",
        "Food & Beverage",
        "Textile",
        "Automotive",
        "Energy",
        "Others"
      ],
      cta: "Join for free",
      successMessage: "Registration completed! We'll contact you soon with new opportunities."
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate CV file size (3MB limit for better performance)
    const maxSize = 3 * 1024 * 1024; // 3MB in bytes
    if (formData.cv && formData.cv.size > maxSize) {
	    const sizeInMB = (formData.cv.size / (1024 * 1024)).toFixed(2);
	    const errorMsg = language === 'es'
		    ? `El CV es demasiado grande (${sizeInMB}MB). Por favor, usa un archivo menor a 3MB.`
		    : `CV file is too large (${sizeInMB}MB). Please use a file smaller than 3MB.`;
	    setMessage(errorMsg);
    	    return; // Stop submission
    }


    setLoading(true);
    setMessage('');

    try {
      console.log('Starting form submission...');
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('geographicArea', formData.geographicArea);
      submitData.append('mainSector', formData.mainSector);
      submitData.append('language', language);
      submitData.append('cv', formData.cv);

      console.log('Sending request to:', `${API}/register-agent`);
      
      // Submit to backend
      const response = await axios.post(`${API}/register-agent`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 90000, // 30 second timeout
        validateStatus: function (status) {
          // Consider 2xx responses as success
          return status >= 200 && status < 300;
        }
      });

      console.log('Response received:', response.status, response.data);

      // Show success message
      setMessage(content[language].successMessage);
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        geographicArea: '',
        mainSector: '',
        cv: null
      });
      
      // Reset file input
      const fileInput = document.getElementById('cv-upload');
      if (fileInput) {
        fileInput.value = '';
      }

    } catch (error) {
      console.error('Submission error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.status,
        responseData: error.response?.data
      });
      
      // Handle network errors and timeouts - backend may have succeeded
      // Common scenarios: ERR_NETWORK, ECONNABORTED, timeout
      if (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ECONNABORTED' ||
        error.message?.includes('timeout') ||
        error.message?.includes('Network Error')
      ) {
        console.log('⚠️ Network/timeout error - backend likely succeeded');
        console.log('Backend takes 8-12 sec (MongoDB + Sheets + Drive + Gmail)');
        console.log('Treating as successful registration');
	console.log('Network error detected, but request may have succeeded');
        setMessage(content[language].successMessage);
        
        // Still reset the form
        setFormData({
          fullName: '',
          email: '',
          geographicArea: '',
          mainSector: '',
          cv: null
        });
        
        const fileInput = document.getElementById('cv-upload');
        if (fileInput) {
          fileInput.value = '';
        }
        
        setLoading(false);
        return;
      }
      
      // Handle actual errors
      let errorMessage = 'Error al enviar el formulario. Inténtalo de nuevo.';
      if (language === 'en') {
        errorMessage = 'Error submitting form. Please try again.';
      }
      
      // Show specific backend error if available
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 400) {
        errorMessage = language === 'es' 
          ? 'Datos inválidos. Verifica el formulario.'
          : 'Invalid data. Please check the form.';
      } else if (error.response?.status === 500) {
        errorMessage = language === 'es'
          ? 'Error del servidor. Por favor, contacta con soporte.'
          : 'Server error. Please contact support.';
      }
      
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section 
      id="registration-section"
      style={{ 
        background: 'var(--pymetra-light-gray)',
        padding: '100px 0'
      }}
    >
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 
            className="display-md"
            style={{ 
              color: 'var(--pymetra-dark-green)',
              marginBottom: '24px'
            }}
          >
            {content[language].title}
          </h2>
          <p className="body-lg" style={{ maxWidth: '600px', margin: '0 auto' }}>
            {content[language].subtitle}
          </p>
        </div>
        
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <form 
            onSubmit={handleSubmit}
            className="card"
            style={{ background: 'var(--white)' }}
          >
            <div className="form-group">
              <label className="form-label">
                {content[language].fields.fullName}
              </label>
              <input
                type="text"
                name="fullName"
                className="form-input"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {content[language].fields.email}
              </label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {content[language].fields.geographicArea}
              </label>
              <input
                type="text"
                name="geographicArea"
                className="form-input"
                value={formData.geographicArea}
                onChange={handleInputChange}
                placeholder={language === 'es' ? 'Ej: Madrid, Barcelona, Valencia...' : 'E.g: Madrid, Barcelona, Valencia...'}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {content[language].fields.mainSector}
              </label>
              <select
                name="mainSector"
                className="form-select"
                value={formData.mainSector}
                onChange={handleInputChange}
                required
              >
                {content[language].sectors.map((sector, index) => (
                  <option key={index} value={index === 0 ? '' : sector}>
                    {sector}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                {content[language].fields.cv}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="file"
                  name="cv"
                  accept=".pdf,.doc,.docx"
                  onChange={handleInputChange}
                  style={{ display: 'none' }}
                  id="cv-upload"
                  required
                />
                <label 
                  htmlFor="cv-upload"
                  className="file-input"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <Upload size={24} color="var(--pymetra-orange)" />
                  <span>
                    {formData.cv 
                      ? formData.cv.name 
                      : (language === 'es' ? 'Seleccionar archivo' : 'Select file')
                    }
                  </span>
                </label>
              </div>
            </div>

            <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: '24px' }} disabled={loading}>
              {loading ? (language === 'es' ? 'Enviando...' : 'Submitting...') : content[language].cta}
            </button>
          </form>
          
          {message && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center',
              backgroundColor: message.includes('Error') || message.includes('error') ? '#fee' : '#efe',
              color: message.includes('Error') || message.includes('error') ? '#c33' : '#363',
              border: `1px solid ${message.includes('Error') || message.includes('error') ? '#fcc' : '#cfc'}`
            }}>
              {message}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RegistrationSection;
